import { FC, useEffect, useMemo, useState } from 'react'
import { UploadPayload, UploadStep } from '..'
import { SelectOption } from '../../../../../../types'
import { Tag } from '../../../../../../../services/cellcast.v2.types'
import Select, { MenuListProps, components } from 'react-select'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import UploadContactFooter from '../Element/UploadContactFooter'
import CreateTagModal, { CreateTagModalMode } from './CreateTagModal'
import { useListTag } from './useListTagHooks'

type SetOptionsProps = {
  activeState: UploadStep
  isLoading: boolean
  selectedTags: SelectOption<string>[]
  selectedList: SelectOption<string>[]
  showList: boolean
  showTag?: boolean
  setActiveState: (state: UploadStep) => void
  setFinalData: (fd: UploadPayload) => void
  setSelectedTags: (tags: SelectOption<string>[]) => void
  setSelectedList: (tags: SelectOption<string>[]) => void
  handleClose: () => void
  handleBack: () => void
  handleNext: () => void
}

const SetOptions: FC<SetOptionsProps> = (props) => {
  const {
    activeState,
    isLoading,
    selectedList,
    selectedTags,
    showList,
    handleClose,
    handleBack,
    handleNext,
    setSelectedTags,
    setSelectedList,
    showTag = true,
  } = props

  const { fetchTagsData, data: tagsData} = useListTag();

  const tags = tagsData?.data ? tagsData.data?.filter((el: Tag) => !el.type || el?.type === 'tag') : []
  const list = tagsData?.data ? tagsData.data?.filter((el: Tag) => el?.type === 'list') : []

  const [createTagMode, setCreateTagMode] = useState(CreateTagModalMode.Tag)
  const [showCreateTagModal, setShowCreateTagModal] = useState<boolean>(false)
  const [usedOptions, setUsedOptions] = useState({
    addTag: false,
    addList: false,
  })

  const toOptions = (tags: Tag[]) => {
    return tags.map<SelectOption<string>>((el) => ({
      label: el.name,
      value: el._id,
    }))
  }

  const MenuItem = (props: MenuListProps<any>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip id='button-tooltip'>Create new tag</Tooltip>}
        >
          <div
            className='select-create-button'
            onClick={() => {
              setCreateTagMode(CreateTagModalMode.Tag)
              setShowCreateTagModal(true)
            }}
          >
            <i className='bi bi-plus-lg'></i>
            <p>Create new</p>
          </div>
        </OverlayTrigger>
      </components.MenuList>
    )
  }

  const MenuListItem = (props: MenuListProps<any>) => {
    return (
      <components.MenuList {...props}>
        {props.children}
        <OverlayTrigger
          placement='top'
          overlay={<Tooltip id='button-tooltip'>Create new list</Tooltip>}
        >
          <div
            className='select-create-button'
            onClick={() => {
              setCreateTagMode(CreateTagModalMode.List)
              setShowCreateTagModal(true)
            }}
          >
            <i className='bi bi-plus-lg'></i>
            <p>Create new</p>
          </div>
        </OverlayTrigger>
      </components.MenuList>
    )
  }

  const CreateModal = useMemo(
    () => (
      <CreateTagModal
        show={showCreateTagModal}
        handleClose={() => {
          setShowCreateTagModal(false)
        }}
        handleCallback={(data: any) => {
          if (data) {
            const newTagList = {label: data?.name, value: data?._id}
            if (createTagMode === CreateTagModalMode.Tag) {
              setSelectedTags([...selectedTags, newTagList])
            } else {
               setSelectedList([...selectedList, newTagList])
            }
          }
          setShowCreateTagModal(false)
          fetchTagsData(null)
        }}
        mode={createTagMode}
      />
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [createTagMode, fetchTagsData, showCreateTagModal]
  )

  useEffect(() => {
    if (activeState === UploadStep.SetOptionTag || activeState === UploadStep.SetOptionList) {
      fetchTagsData(null)

      const tmpOptions = { ...usedOptions }
      if (selectedTags.length > 0) {
        tmpOptions.addTag = true
      }

      if (selectedList.length > 0) {
        tmpOptions.addList = true
      }

      setUsedOptions(tmpOptions)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeState])

  return (
    <div className='modal-body p-15'>
      {CreateModal}
      <div className='advance_setting'>
        {showTag && (
          <div className='form-check form-check-sm form-check-custom form-check-solid ps-7 mb-5'>
            <input
              onChange={(e) => {
                const value = e.currentTarget.value
                if (value === 'on') {
                  setUsedOptions({ ...usedOptions, addTag: true })
                } else {
                  setUsedOptions({ ...usedOptions, addTag: false })
                  setSelectedTags([])
                }
              }}
              className='form-check-input me-5'
              id='tags'
              type='checkbox'
              value={usedOptions.addTag ? 'off' : 'on'}
              checked={usedOptions.addTag}
            />
            <div>
              <label htmlFor='tags' style={{ lineHeight: '0.65' }}>
                Add "Tag" (optional)
                <span className='ms-2'>
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      opacity='0.3'
                      d='M8 15.5C12.1421 15.5 15.5 12.1421 15.5 8C15.5 3.85786 12.1421 0.5 8 0.5C3.85786 0.5 0.5 3.85786 0.5 8C0.5 12.1421 3.85786 15.5 8 15.5Z'
                      fill='#838492'
                    />
                    <path
                      d='M8 10.2497C7.85081 10.2497 7.70774 10.1904 7.60225 10.0849C7.49676 9.97945 7.4375 9.83638 7.4375 9.68719V9.07219C7.4305 8.69625 7.52949 8.32593 7.72314 8.00362C7.9168 7.68131 8.19728 7.42003 8.5325 7.24969C8.72899 7.15461 8.89643 7.00865 9.01744 6.82698C9.13845 6.64531 9.20862 6.43455 9.22064 6.2166C9.23266 5.99864 9.18611 5.78144 9.08581 5.58756C8.98552 5.39368 8.83514 5.23019 8.6503 5.11408C8.46546 4.99796 8.25291 4.93345 8.03471 4.92725C7.81651 4.92106 7.60063 4.97339 7.4095 5.07883C7.21837 5.18427 7.05895 5.33895 6.94781 5.52683C6.83667 5.7147 6.77787 5.92891 6.7775 6.14719C6.7775 6.29638 6.71823 6.43945 6.61274 6.54494C6.50726 6.65043 6.36418 6.70969 6.215 6.70969C6.06581 6.70969 5.92274 6.65043 5.81725 6.54494C5.71176 6.43945 5.6525 6.29638 5.6525 6.14719C5.65375 5.80373 5.73012 5.46469 5.87624 5.15386C6.02237 4.84303 6.23472 4.56792 6.4984 4.34783C6.76208 4.12773 7.07072 3.96798 7.40267 3.87977C7.73461 3.79156 8.08184 3.77703 8.42 3.83719C8.89211 3.92601 9.32594 4.15664 9.66361 4.49834C10.0013 4.84004 10.2268 5.27656 10.31 5.74969C10.4046 6.25535 10.3294 6.7781 10.0961 7.23658C9.8628 7.69506 9.48447 8.06355 9.02 8.28469C8.87783 8.36211 8.75985 8.47735 8.6791 8.61766C8.59836 8.75796 8.558 8.91787 8.5625 9.07969V9.69469C8.56052 9.84257 8.50039 9.98373 8.39512 10.0876C8.28984 10.1915 8.14789 10.2497 8 10.2497Z'
                      fill='#838492'
                    />
                    <path
                      d='M7.99999 12.1924C8.31065 12.1924 8.56249 11.9405 8.56249 11.6299C8.56249 11.3192 8.31065 11.0674 7.99999 11.0674C7.68933 11.0674 7.43749 11.3192 7.43749 11.6299C7.43749 11.9405 7.68933 12.1924 7.99999 12.1924Z'
                      fill='#7950F2'
                    />
                  </svg>
                </span>
              </label>
              <p>
                A or create a new "Tag" to these contacts. Tagging your contacts helps better identify
                them and create segments
              </p>
              {usedOptions.addTag && (
                <Select
                  value={selectedTags}
                  components={{ MenuList: MenuItem }}
                  classNamePrefix='createBox'
                  isMulti
                  options={toOptions(tags || [])}
                  className='selectbox-create-custom'
                  onChange={(e: any) => {
                    setSelectedTags(e)
                  }}
                />
              )}
            </div>
          </div>
        )}
        {showList && (
          <div className='form-check form-check-sm form-check-custom form-check-solid ps-7'>
            <input
              onChange={(e) => {
                const value = e.currentTarget.value
                if (value === 'on') {
                  setUsedOptions({ ...usedOptions, addList: true })
                } else {
                  setUsedOptions({ ...usedOptions, addList: false })
                  setSelectedList([])
                }
              }}
              className='form-check-input me-5'
              id='list'
              type='checkbox'
              value={usedOptions.addList ? 'off' : 'on'}
              checked={usedOptions.addList}
            />
            <div className='col-12'>
              <label htmlFor='list' style={{ lineHeight: '0.65' }}>
                Add contact to a "list" (optional)
                <span className='ms-2'>
                  <svg
                    width='16'
                    height='16'
                    viewBox='0 0 16 16'
                    fill='none'
                    xmlns='http://www.w3.org/2000/svg'
                  >
                    <path
                      opacity='0.3'
                      d='M8 15.5C12.1421 15.5 15.5 12.1421 15.5 8C15.5 3.85786 12.1421 0.5 8 0.5C3.85786 0.5 0.5 3.85786 0.5 8C0.5 12.1421 3.85786 15.5 8 15.5Z'
                      fill='#838492'
                    />
                    <path
                      d='M8 10.2497C7.85081 10.2497 7.70774 10.1904 7.60225 10.0849C7.49676 9.97945 7.4375 9.83638 7.4375 9.68719V9.07219C7.4305 8.69625 7.52949 8.32593 7.72314 8.00362C7.9168 7.68131 8.19728 7.42003 8.5325 7.24969C8.72899 7.15461 8.89643 7.00865 9.01744 6.82698C9.13845 6.64531 9.20862 6.43455 9.22064 6.2166C9.23266 5.99864 9.18611 5.78144 9.08581 5.58756C8.98552 5.39368 8.83514 5.23019 8.6503 5.11408C8.46546 4.99796 8.25291 4.93345 8.03471 4.92725C7.81651 4.92106 7.60063 4.97339 7.4095 5.07883C7.21837 5.18427 7.05895 5.33895 6.94781 5.52683C6.83667 5.7147 6.77787 5.92891 6.7775 6.14719C6.7775 6.29638 6.71823 6.43945 6.61274 6.54494C6.50726 6.65043 6.36418 6.70969 6.215 6.70969C6.06581 6.70969 5.92274 6.65043 5.81725 6.54494C5.71176 6.43945 5.6525 6.29638 5.6525 6.14719C5.65375 5.80373 5.73012 5.46469 5.87624 5.15386C6.02237 4.84303 6.23472 4.56792 6.4984 4.34783C6.76208 4.12773 7.07072 3.96798 7.40267 3.87977C7.73461 3.79156 8.08184 3.77703 8.42 3.83719C8.89211 3.92601 9.32594 4.15664 9.66361 4.49834C10.0013 4.84004 10.2268 5.27656 10.31 5.74969C10.4046 6.25535 10.3294 6.7781 10.0961 7.23658C9.8628 7.69506 9.48447 8.06355 9.02 8.28469C8.87783 8.36211 8.75985 8.47735 8.6791 8.61766C8.59836 8.75796 8.558 8.91787 8.5625 9.07969V9.69469C8.56052 9.84257 8.50039 9.98373 8.39512 10.0876C8.28984 10.1915 8.14789 10.2497 8 10.2497Z'
                      fill='#838492'
                    />
                    <path
                      d='M7.99999 12.1924C8.31065 12.1924 8.56249 11.9405 8.56249 11.6299C8.56249 11.3192 8.31065 11.0674 7.99999 11.0674C7.68933 11.0674 7.43749 11.3192 7.43749 11.6299C7.43749 11.9405 7.68933 12.1924 7.99999 12.1924Z'
                      fill='#7950F2'
                    />
                  </svg>
                </span>
              </label>
              <p>
                Assign or create a new "List" for your contacts. This will help you group and
                identify these contacts.
              </p>
              {usedOptions.addList && (
                <Select
                  value={selectedList}
                  components={{ MenuList: MenuListItem }}
                  classNamePrefix='createBox'
                  isMulti
                  options={toOptions(list || [])}
                  className='selectbox-create-custom'
                  onChange={(e: any) => setSelectedList(e)}
                />
              )}
            </div>
          </div>
        )}
      </div>
      <UploadContactFooter
        allowedNext={!isLoading}
        handleClose={handleClose}
        handleNext={handleNext}
        handleBack={handleBack}
      />
    </div>
  )
}

SetOptions.defaultProps = {
  showList: true,
}

export default SetOptions
